import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Main } from './components/layout/Main';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import { ProfilePage } from './components/profile/ProfilePage';
import { BillingPage } from './components/profile/BillingPage';
import { DeleteProfilePage } from './components/profile/DeleteProfilePage';
import { SharedTemplatePage } from './components/templates/SharedTemplatePage';
import { VerifyEmailPage } from './components/profile/VerifyEmailPage';
import { useAuthStore } from './stores/authStore';

const AUTHENTICATION_RETURN_TO_KEY = 'dotstorming_authentication_return_to';

const UnknownRouteRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/main${search}`} replace />;
};

const RequireAuthenticatedUser = ({ children }: { children: ReactNode }) => {
  const { pathname, search } = useLocation();
  const { user } = useAuthStore();

  if (!user) {
    const returnTo = `${pathname}${search}`;
    window.sessionStorage.setItem(AUTHENTICATION_RETURN_TO_KEY, returnTo);
    const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    window.location.replace(loginUrl);
    return null;
  }

  return children;
};

const App = () => {
  const isAuthenticationChecked = useAuthBootstrap();

  if (!isAuthenticationChecked) {
    return null; // or a loading spinner
  }

  return (
    <Routes>
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/"
        element={
          <RequireAuthenticatedUser>
            <Main />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/archived"
        element={
          <RequireAuthenticatedUser>
            <Main />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/templates"
        element={
          <RequireAuthenticatedUser>
            <Main />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/templates/shared/:token"
        element={
          <RequireAuthenticatedUser>
            <SharedTemplatePage />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/main"
        element={
          <RequireAuthenticatedUser>
            <Main />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuthenticatedUser>
            <ProfilePage />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireAuthenticatedUser>
            <BillingPage />
          </RequireAuthenticatedUser>
        }
      />
      <Route
        path="/delete"
        element={
          <RequireAuthenticatedUser>
            <DeleteProfilePage />
          </RequireAuthenticatedUser>
        }
      />
      <Route path="*" element={<UnknownRouteRedirect />} />
    </Routes>
  );
};

export default App;
